import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { StorageService } from '@coworkrz/shared/misc';
import { jwtDecode } from 'jwt-decode';

import { AUTH_ACCESS_TOKEN_KEY, AUTH_REFRESH_TOKEN_KEY, Token } from './tokens';
import { GoogleUserCredentials } from './credentials';
import { Observable, map, of } from 'rxjs';
import { User } from '@coworkrz/users/types';
import { UserService } from '@coworkrz/users/data-access';
import { EmailVerification } from './verification';
import { Card } from '@coworkrz/cards/data-access';

interface AuthState {
  userId: string;
  expiresAt: Date;
  access: string;
  refresh: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private storage = inject(StorageService);
  private userService = inject(UserService);
  private accessTokenKey = inject<string>(AUTH_ACCESS_TOKEN_KEY, {
    optional: true,
  }) as string;
  private refreshTokenKey = inject(AUTH_REFRESH_TOKEN_KEY, {
    optional: true,
  }) as string;

  private state?: AuthState;
  private user?: User;
  private card?: Card;
  private url = 'auth';

  loginWithEmail(email: string) {
    return this.http.post(`${this.url}/login`, { email });
  }

  authenticateWithGoogle(
    credentials: GoogleUserCredentials
  ): Observable<Token> {
    return this.http
      .post<{ [key: string]: string }>(`${this.url}/google/`, {
        credential: credentials.credential,
      })
      .pipe(
        map((response: { [key: string]: string }) => {
          const token: Token = {
            access: response[this.accessTokenKey],
            refresh: response[this.refreshTokenKey],
          };

          return token;
        })
      );
  }

  signupWithGoogle(credentials: GoogleUserCredentials): Observable<Token> {
    return this.http
      .post<{ [key: string]: string }>(`${this.url}/google/signup`, {
        credential: credentials.credential,
      })
      .pipe(
        map((response: { [key: string]: string }) => {
          const token: Token = {
            access: response[this.accessTokenKey],
            refresh: response[this.refreshTokenKey],
          };

          return token;
        })
      );
  }

  signupWithEmail(
    user: Partial<User>,
    params: { [key: string]: boolean }
  ): Observable<Partial<User>> {
    let httpParams = new HttpParams();
    for (const key in params) {
      httpParams = httpParams.append(key, params[key]);
    }
    return this.http.post<Partial<User>>(`${this.url}/email/signup`, user, {
      params: httpParams,
    });
  }

  signupWithQrCode(cardNumber: string): Observable<Partial<User>> {
    return this.http.post(`${this.url}/qrcode/signup/${cardNumber}`, {});
  }

  verifyToken(object: EmailVerification): Observable<Token> {
    return this.http
      .post<{ [key: string]: string }>(
        `${this.url}/email/token/verification`,
        object
      )
      .pipe(
        map((response: { [key: string]: string }) => {
          const token: Token = {
            access: response[this.accessTokenKey],
            refresh: response[this.refreshTokenKey],
          };

          return token;
        })
      );
  }

  signinWithQrCode(cardNumber: string): Observable<Partial<User>> {
    return this.http.post(`${this.url}/qrcode/signin`, { cardNumber });
  }

  me(): Observable<{ user: User; card: Card }> {
    return this.http.get<{ user: User; card: Card }>(`${this.url}/me`);
  }

  login(token: Token): boolean {
    if (
      token.access &&
      token.refresh &&
      this.initSession(token.access, token.refresh)
    ) {
      this.storage.setItem(this.accessTokenKey, token.access);
      this.storage.setItem(this.refreshTokenKey, token.refresh);

      return true;
    }

    return false;
  }

  getAccessToken(): string {
    if (this.state?.access) {
      return this.state.access;
    }

    return '';
  }

  isAuthenticated(force = false): boolean {
    if (force) {
      this.initSession();
    }

    const isValidState = Boolean(this.state?.access && this.state?.refresh);
    const isNotExpired = Boolean(
      this.state && this.state.expiresAt > new Date()
    );

    return isValidState && isNotExpired;
  }

  signOut(): void {
    this.state = undefined;
    this.storage.clear();
  }

  loadUser(): Observable<User> {
    if (this.user) {
      return of(this.user);
    }

    return this.me().pipe(
      map((user) => {
        this.user = user.user;
        return user.user;
      })
    );
  }

  loadCard(): Observable<Card> {
    if (this.card) {
      return of(this.card);
    }

    return this.me().pipe(
      map((card) => {
        this.card = card.card;
        return card.card;
      })
    );
  }

  private initSession(accessToken?: string, refreshToken?: string): boolean {
    // Load the tokens from the storage if not provided.
    const access = accessToken ?? this.storage.getItem(this.accessTokenKey);
    const refresh = refreshToken ?? this.storage.getItem(this.refreshTokenKey);

    // We need both tokens to be available
    if (!access || !refresh) {
      return false;
    }

    try {
      const payload: { [key: string]: string } = jwtDecode(access);
      if (refresh && access && payload['sub'] && payload['exp']) {
        this.state = {
          userId: payload['sub'],
          expiresAt: new Date(Number.parseInt(payload['exp']) * 1000),
          refresh,
          access,
        };
        return true;
      }
    } catch (error) {
      return false;
    }

    return false;
  }
}
